import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, ACTIVE, INACTIVE, VIEW, EDIT,
} from "const"
import { mtUsersRoute } from "client/parts/routes"
import { MtUser, MtUserCreate } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    UserValidation,
    LangType,

    LoadUserByLoginOrEmail,
    LoadRegisteredUserByEmail,
    SaveUser,

    closeModal,
    setUserEdit,
    setUserReloadList,
    setUserReloadView,
    setUserReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditUserProps {
    user: MtUser
}

let isMounted: boolean

const Form = ({
    user,
}: EditUserProps) => {
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [firstName, setFirstName] = useInput(user.firstName)
    const [lastName, setLastName] = useInput(user.lastName)
    const [login, setLogin] = useInput(user.login)
    const [email, setEmail] = useInput(user.email)

    const [status, setStatus] = useState(user.status)
    const [validation, setValidation] = useState<UserValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            dispatch(closeModal())
            return
        }

        const path = `${mtUsersRoute}/${VIEW}/${user.id}`
        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newUserData: MtUserCreate = {
            firstName,
            lastName,
            login,
            email,
            logoId: user.logoId || null,
            status,
        }

        Promise.all([
            // check if the entered email is reserved by registered user
            LoadUserByLoginOrEmail(email.length ? email : "0"),
            LoadRegisteredUserByEmail(email.length ? email : "0"),
        ]).then(([existingUser, registeredUser]) => {
            if (
                (existingUser && existingUser.id !== user.id)
                || registeredUser
            ) {
                setValidation({ email: lang.userWithEmailExists })
                setSaveState(IDLE)
                setApplyState(IDLE)
                return
            }

            SaveUser(user.id, newUserData, lang)
            .then((res) => {
                if (isMounted) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)

                    if (res.newUserId) replaceHistoryState(`${mtUsersRoute}/${EDIT}/${res.newUserId}`)
                }
                if (res.validation) return

                dispatch(setUserEdit({
                    id: res.newUserId || user.id,
                    ...newUserData,
                }))

                dispatch(setUserReloadList(false))
                dispatch(setUserReloadView(false))
                setTimeout(() => {
                    dispatch(setUserReloadList(true))
                    dispatch(setUserReloadView(true))
                }, 0)

                if (mode === 1) onClose()
                if (mode === 2) {
                    dispatch(setUserReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setUserReloadEdit(true))
                    }, 0)
                }
            })
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.firstName}</Label>
                        <Input type="text" value={firstName} onChange={setFirstName} />
                        <Feedback>{validation && validation.firstName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.lastName}</Label>
                        <Input type="text" value={lastName} onChange={setLastName} />
                        <Feedback>{validation && validation.lastName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.login}</Label>
                        <Input type="text" value={login} onChange={setLogin} />
                        <Feedback>{validation && validation.login}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.email}</Label>
                        <Input type="text" value={email} onChange={setEmail} />
                        <Feedback>{validation && validation.email}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <SelectDropdown title={lang[status as keyof LangType] || ""}>
                            <SelectDropdownItem onClick={() => setStatus(ACTIVE)}>{lang[ACTIVE]}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setStatus(INACTIVE)}>{lang[INACTIVE]}</SelectDropdownItem>
                        </SelectDropdown>
                        <Feedback>{validation && validation.status}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
